<template>
  <div class="admin-reports">
    <h1>Admin Reports</h1>
    <section class="box">
      <h2>Channel Followers</h2>
      <div class="controls">
        <label><input type="checkbox" v-model="includeEmails"> include emails (limited)</label>
        <label>Email limit <input type="number" v-model.number="emailLimit" min="1" max="100" style="width:80px"></label>
        <button @click="loadChannelFollowers" :disabled="loadingFollowers">Load</button>
      </div>
      <div v-if="loadingFollowers">Loading...</div>
      <table v-else>
        <thead>
          <tr>
            <th>Channel</th>
            <th>Followers</th>
            <th v-if="includeEmails">Emails</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in channelFollowers" :key="it.channel_id">
            <td>{{ it.title }} ({{ it.channel_id }})</td>
            <td>{{ it.follower_count }}</td>
            <td v-if="includeEmails"><div class="emails">{{ (it.follower_emails||[]).join(', ') }}</div></td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="mt-6">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">Channel Summaries</h2>
        <RouterLink to="/admin/channel-summaries" class="text-sm text-indigo-700 hover:underline">Open full page →</RouterLink>
      </div>

      <div v-if="loadingSummaries" class="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-600">Loading...</div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-for="g in channelSummaries" :key="g.channel_id" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 border-b border-gray-100 flex items-start justify-between">
            <div>
              <div class="text-sm text-gray-500">{{ g.channel_id }}</div>
              <div class="text-base font-semibold text-gray-900">{{ g.channel_title || 'Untitled Channel' }}</div>
            </div>
            <span class="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">{{ g.items?.length || 0 }} videos</span>
          </div>
          <ul class="divide-y divide-gray-100">
            <li v-for="it in g.items" :key="it.video_id" class="p-3 hover:bg-gray-50">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <RouterLink :to="createVideoUrl(it.title || '', it.video_id, 'tr')" class="text-sm font-mono text-indigo-700 hover:underline">
                    {{ it.video_id }}
                  </RouterLink>
                  <div class="text-sm text-gray-700 truncate max-w-full">{{ it.title || 'No title' }}</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- API Usage Report Section -->
    <section class="mt-6">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">API Usage Report</h2>
        <button @click="loadApiUsageReport" :disabled="loadingApiUsage" class="text-sm text-indigo-700 hover:underline">
          {{ loadingApiUsage ? 'Loading...' : 'Refresh' }}
        </button>
      </div>

      <div v-if="loadingApiUsage" class="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-600">Loading API usage data...</div>

      <div v-else-if="apiUsageReport" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <!-- Summary Card -->
        <div class="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="text-center">
              <div class="text-2xl font-bold text-blue-600">{{ apiUsageReport.total_remaining }}</div>
              <div class="text-sm text-gray-600">Total Remaining</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold text-green-600">{{ apiUsageReport.total_keys }}</div>
              <div class="text-sm text-gray-600">Total Keys</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold text-purple-600">{{ apiUsageReport.service_name }}</div>
              <div class="text-sm text-gray-600">Service</div>
            </div>
          </div>
        </div>

        <!-- API Keys Table -->
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage Count</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(key, index) in apiUsageReport.api_keys" :key="index" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{{ key.key_value }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ key.usage_count }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="[
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    key.remaining > 20 ? 'bg-green-100 text-green-800' : 
                    key.remaining > 10 ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-red-100 text-red-800'
                  ]">
                    {{ key.remaining }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-600">
        No API usage data available. Click "Refresh" to load data.
      </div>
    </section>
    <div class="controls">
      <label>
        Job Name
        <select v-model="jobName">
          <option value="channel_refresh">channel_refresh</option>
          <option value="transcript_check">transcript_check</option>
          <option value="summary_check">summary_check</option>
        </select>
      </label>
      <label>
        Filter by Channel Name
        <input v-model="filterText" placeholder="e.g. TEDx Talks" />
      </label>
      <button @click="loadLastRun" :disabled="loading">Load Last Run</button>
    </div>

    <div v-if="loading">Loading...</div>

    <div v-if="displayedItems.length">
      <h3>Items</h3>
      <table>
        <thead>
          <tr>
            <th>created_at</th>
            <th>op</th>
            <th>entity_type</th>
            <th>table</th>
            <th>channel_id</th>
            <th>channel_name</th>
            <th>video_id</th>
            <th>details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in displayedItems" :key="it.id">
            <td>{{ it.created_at }}</td>
            <td>{{ it.op }}</td>
            <td>{{ it.entity_type }}</td>
            <td>{{ it.table_name }}</td>
            <td>{{ it.channel_id }}</td>
            <td>{{ getChannelName(it.channel_id) }}</td>
            <td>{{ it.video_id }}</td>
            <td><pre class="details">{{ it.details }}</pre></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="!loading">
      <p>No items found for the last run.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { supabase } from '@/config/supabase'
import api from '@/services/apiService'
import { createVideoUrl } from '@/utils/helpers'

const jobName = ref<string>('transcript_check')
const loading = ref(false)
const run = ref<any | null>(null)
const items = ref<any[]>([])
const filterText = ref('')
const channelNameById = ref<Record<string, string>>({})

// Admin: channel followers
const includeEmails = ref<boolean>(false)
const emailLimit = ref<number>(20)
const loadingFollowers = ref<boolean>(false)
const channelFollowers = ref<Array<{channel_id:string; title:string; follower_count:number; follower_emails?:string[]}>>([])

async function loadChannelFollowers() {
  loadingFollowers.value = true
  try {
    const qs = `?includeEmails=${includeEmails.value}&emailLimit=${emailLimit.value}`
    const res = await api.get(`/api/admin/channel-followers${qs}`)
    channelFollowers.value = res?.items || []
  } catch (e) {
    console.error('Load channel followers error', e)
    channelFollowers.value = []
  } finally {
    loadingFollowers.value = false
  }
}

// Admin: channel summaries per channel
const perChannelLimit = ref<number>(20)
const loadingSummaries = ref<boolean>(false)
const channelSummaries = ref<Array<{ channel_id:string; channel_title?:string|null; items: Array<{ video_id: string; title?: string|null }> }>>([])

async function loadChannelSummaries() {
  loadingSummaries.value = true
  try {
    const res = await api.get(`/api/admin/channel-summaries?perChannelLimit=${perChannelLimit.value}`)
    channelSummaries.value = res?.items || []
  } catch (e) {
    console.error('Load channel summaries error', e)
    channelSummaries.value = []
  } finally {
    loadingSummaries.value = false
  }
}

// Admin: API usage report
const loadingApiUsage = ref<boolean>(false)
const apiUsageReport = ref<any>(null)

async function loadApiUsageReport() {
  loadingApiUsage.value = true
  try {
    const res = await api.get('/api/admin/api-usage-report')
    apiUsageReport.value = res
  } catch (e) {
    console.error('Load API usage report error', e)
    apiUsageReport.value = null
  } finally {
    loadingApiUsage.value = false
  }
}

function getChannelName(id?: string) {
  if (!id) return ''
  return channelNameById.value[id] || ''
}

const displayedItems = computed(() => {
  const q = filterText.value.trim().toLowerCase()
  if (!q) return items.value
  return items.value.filter(it => (getChannelName(it.channel_id) || '').toLowerCase().includes(q))
})

async function hydrateChannelNames(fromItems: any[]) {
  const ids = Array.from(new Set(fromItems.map(i => i.channel_id).filter(Boolean))) as string[]
  if (!ids.length) return
  const { data, error } = await supabase
    .from('channels')
    .select('id,title')
    .in('id', ids)
  if (error) {
    console.error('Load channels error', error)
    return
  }
  const map: Record<string, string> = {}
  for (const row of data || []) {
    map[row.id] = row.title
  }
  channelNameById.value = map
}

async function loadLastRun() {
  loading.value = true
  run.value = null
  items.value = []
  channelNameById.value = {}
  try {
    const { data: runs, error: runErr } = await supabase
      .from('job_runs')
      .select('id')
      .eq('job_name', jobName.value)
      .order('started_at', { ascending: false })
      .limit(1)
    if (runErr) throw runErr
    const lastRun = runs?.[0]
    run.value = lastRun || null
    if (!lastRun?.id) return

    const { data: itemRows, error: itemErr } = await supabase
      .from('job_run_items')
      .select('*')
      .eq('run_id', lastRun.id)
      .order('created_at', { ascending: false })
      .limit(200)
    if (itemErr) throw itemErr
    items.value = itemRows || []
    await hydrateChannelNames(items.value)
  } catch (e) {
    console.error('Load report error', e)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const saved = localStorage.getItem('adminReports.jobName')
  if (saved) jobName.value = saved
  loadLastRun()
  loadApiUsageReport()
})

watch(jobName, (val) => {
  localStorage.setItem('adminReports.jobName', val)
  loadLastRun()
})
</script>

<style scoped>
.admin-reports { padding: 16px; margin-top: 80px; }
.controls { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; background: rgba(255,255,255,0.9); padding: 8px 12px; border-radius: 8px; position: relative; z-index: 1; }
.controls input { padding: 6px 8px; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; vertical-align: top; }
pre { margin: 0; white-space: pre-wrap; word-break: break-word; }
pre.details { max-width: 420px; }
</style> 